import {
  BILD_FORMATE,
  RATGEBER_VERZEICHNISSE,
  verzeichnisText,
  type AuftragsTyp,
} from '@awf/shared';
import { istAuftragsTyp } from '../auftragstypen.js';
import { GRENZEN, type FormularWerte } from './validate.js';

export interface FeldSpalte {
  name: keyof FormularWerte;
  beschriftung: string;
  art: 'text' | 'mehrzeilig' | 'auswahl' | 'schalter';
  pflicht?: boolean;
  hinweis?: string;
  platzhalter?: string;
  optionen?: { wert: string; text: string }[];
  grenze?: number;
  /**
   * Steht in der Briefing-Karte der Fortschrittsseite; der Rest nur im Vergroessern-Dialog.
   * Traegt ein Typ kein solches Feld, stehen dort alle (wenige) Felder.
   */
  kernfeld?: true;
  /**
   * Gegenseitiger Ausschluss: Traegt `feld` einen Wert, ist dieses Feld gesperrt und zeigt statt
   * seines Hinweises `hinweis`. Leeren gibt es wieder frei.
   */
  gesperrtDurch?: { feld: keyof FormularWerte; hinweis: string };
}

const THEMA: FeldSpalte = {
  name: 'thema',
  beschriftung: 'Thema',
  art: 'text',
  pflicht: true,
  platzhalter: 'z. B. Was tun bei Hagelschaden am Auto?',
  grenze: GRENZEN.thema,
};

export const FELDSAETZE: Record<AuftragsTyp, FeldSpalte[]> = {
  // Kernfelder wie die vier der Content-Factory (Thema, Kernbotschaft, Ziel, Anlass): eine
  // Zeile in der Briefing-Karte statt zwei (Rueckmeldung 2026-10-13).
  ratgeber: [
    { ...THEMA, kernfeld: true },
    {
      kernfeld: true,
      name: 'contentTyp',
      beschriftung: 'Content-Typ',
      art: 'auswahl',
      // Im Backend OPTIONAL. Das Mock-up markiert es mit Sternchen, das ist falsch.
      optionen: [
        { wert: '', text: 'Ohne Angabe' },
        { wert: 'evergreen', text: 'Evergreen Content' },
        { wert: 'news', text: 'News-Artikel' },
      ],
    },
    {
      kernfeld: true,
      name: 'sprache',
      beschriftung: 'Sprache',
      art: 'auswahl',
      optionen: [
        { wert: 'de', text: 'Deutsch' },
        { wert: 'en', text: 'Englisch' },
      ],
    },
    {
      name: 'zusatzinfos',
      beschriftung: 'Zusätzliche Informationen zum Thema',
      art: 'mehrzeilig',
      grenze: GRENZEN.zusatzinfos,
    },
    {
      kernfeld: true,
      name: 'produkte',
      beschriftung: 'Versicherungsprodukt',
      art: 'text',
      hinweis: 'Leer lassen oder bis zu zwei Produkte, mit Komma getrennt.',
      grenze: GRENZEN.produkte,
    },
    {
      name: 'zielgruppe',
      beschriftung: 'Zielgruppe',
      art: 'text',
      hinweis: 'Werte, Lebensstil, Bedürfnisse, Motivation.',
      grenze: GRENZEN.zielgruppe,
    },
    { name: 'longtailBevorzugt', beschriftung: 'Longtail-Keywords bevorzugen', art: 'schalter' },
    {
      name: 'ausschlussKeywords',
      beschriftung: 'Ausschluss-Keywords',
      art: 'text',
      grenze: GRENZEN.ausschlussKeywords,
    },
    {
      name: 'quellUrl',
      beschriftung: 'Quell-URL',
      art: 'text',
      hinweis: 'Nur Adressen auf ergo.de, mit https.',
      grenze: GRENZEN.quellUrl,
    },
    { name: 'sharepointAblage', beschriftung: 'Zusätzlich in SharePoint ablegen', art: 'schalter' },
  ],

  // Alles Weitere wertet die Bilder-Kette nicht aus. Es anzuzeigen wuerde Erwartungen
  // wecken, die sie nicht erfuellt.
  bilder: [
    {
      ...THEMA,
      // Mehrzeilig: Hier steht eine Bildbeschreibung, kein Titel (Rueckmeldung 2026-10-13).
      art: 'mehrzeilig',
      platzhalter: 'z. B. Familie im Garten, Sommer, natürliches Licht',
    },
    {
      name: 'format',
      beschriftung: 'Format',
      art: 'auswahl',
      pflicht: true,
      optionen: BILD_FORMATE.map((f) => ({ wert: f, text: f })),
    },
  ],

  analyse: [
    {
      name: 'verzeichnis',
      beschriftung: 'Verzeichnis',
      art: 'auswahl',
      // Dieselbe Liste wie `productMapping` in n8n, siehe RATGEBER_VERZEICHNISSE.
      optionen: [{ wert: '', text: 'Kein Verzeichnis' }, ...RATGEBER_VERZEICHNISSE],
      hinweis: 'Für einen Sammellauf über alle Ratgeber eines Verzeichnisses.',
      // Eine Analyse prueft genau eine Sache (Vorgabe 2026-10-16), Backend wie validate.ts.
      gesperrtDurch: {
        feld: 'quellUrl',
        hinweis: 'Gesperrt, solange eine Ratgeber-URL eingetragen ist.',
      },
    },
    {
      name: 'quellUrl',
      beschriftung: 'Ratgeber-URL',
      art: 'text',
      hinweis: 'Für die Analyse einer einzelnen Seite auf ergo.de.',
      gesperrtDurch: {
        feld: 'verzeichnis',
        hinweis: 'Gesperrt, solange ein Verzeichnis gewählt ist.',
      },
      platzhalter: 'https://www.ergo.de/ratgeber/…',
      grenze: GRENZEN.quellUrl,
    },
  ],

  pdp: [
    {
      name: 'quellUrl',
      beschriftung: 'PDP-URL',
      art: 'text',
      pflicht: true,
      hinweis: 'Die Adresse einer Produktseite auf ergo.de (…/Produkte/…).',
      platzhalter: 'https://www.ergo.de/de/Produkte/…',
      grenze: GRENZEN.quellUrl,
    },
  ],
};

/**
 * Gesperrt, solange das Gegenfeld einen Wert traegt UND dieses Feld leer ist (Codex-Gate PR #91):
 * Stehen in einem vorbelegten Zustand beide Werte, sperrten sich sonst beide gegenseitig, und
 * keiner liesse sich mehr leeren. Dann bleiben beide frei, und die Pruefung verlangt, einen zu
 * entfernen.
 */
export function istGesperrt(spalte: FeldSpalte, werte: FormularWerte): boolean {
  const sperre = spalte.gesperrtDurch;
  if (!sperre) return false;
  return String(werte[sperre.feld]).trim() !== '' && String(werte[spalte.name]).trim() === '';
}

// Bleibt als Name bestehen (Formular und Tests importieren ihn); die Wahrheit liegt im Register.
export function istGueltigerTyp(roh: string): roh is AuftragsTyp {
  return istAuftragsTyp(roh);
}

/**
 * Bei `analyse` wird `thema` von der Kette mit URL bzw. Verzeichnisname ueberschrieben
 * (Story 7L2cRP40fqBOBsXeUngo). Statt ein Pflichtfeld anzuzeigen, dessen Inhalt garantiert
 * verworfen wird, leitet das Formular denselben Wert selbst ab -- dann stimmt die Anzeige in
 * der Liste von Anfang an mit dem ueberein, was die Kette spaeter hineinschreibt.
 */
export function themaAus(werte: FormularWerte): string {
  if (werte.typ === 'pdp') return werte.quellUrl.trim();
  if (werte.typ !== 'analyse') return werte.thema.trim();
  return verzeichnisText(werte.verzeichnis) ?? werte.quellUrl.trim();
}
